import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components/Markdown";
import { useAnchor } from "hooks";
import { Features14AContent } from "types/graphql/generated";

interface Props {
  content: Features14AContent;
}

const Features14a: VFC<Props> = withErrorBoundary(({ content: { title, description, features14FeatureList } }) => {
  useAnchor("features14");

  return (
    <StyledFeatures14 className="features14">
      <div className="Features14__container Content">
        <div className=" Features14__baseInfo">
          <h2 className="Features14__title Font_48_54_600 lg:Font_40_48_600">{title}</h2>
          <Markdown className="Features14__mobileTitle md:Font_32_40_600">{title.split(". ").join(".<br>")}</Markdown>
          <Markdown className="Features14__description Color_secondary Font_24_32_450 md:Font_18_26_450">
            {description}
          </Markdown>
        </div>
        <ul className="Features14__featuresContainer">
          {features14FeatureList.map(({ featureTitle, featureDescription, feature, id }) => (
            <li key={id} className="Features14__feature">
              <p className="Features14__featureTitle Font_18_26_600">{featureTitle}</p>
              <h3 className="Features14__featureName Font_24_32_600 xl:Font_24_32_600">{feature}</h3>
              <Markdown className="Features14__featureDescription Color_secondary Font_18_26_450">
                {featureDescription}
              </Markdown>
            </li>
          ))}
        </ul>
      </div>
    </StyledFeatures14>
  );
}, {});

const StyledFeatures14 = styled.section`
  .Features14__title {
    max-width: 957px;
  }

  .Features14__mobileTitle {
    display: none;
  }

  .Features14__description {
    max-width: 587px;
    margin-top: 24px;
  }

  .Features14__featuresContainer {
    display: flex;
    margin-top: 48px;
  }

  .Features14__feature {
    flex: 1;
    padding: 40px 32px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["60"]};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.fog["05"]};
  }

  .Features14__feature:not(:first-child) {
    margin-left: 32px;
  }

  .Features14__featureTitle {
    width: fit-content;
    padding: 8px 20px;
    border-radius: 32px;
  }

  .Features14__feature:nth-child(odd) {
    .Features14__featureTitle {
      background: ${({ theme }) => theme.colors.eucalyptus["80"]};
      color: ${({ theme }) => theme.colors.eucalyptus["05"]};
    }
  }

  .Features14__feature:nth-child(even) {
    .Features14__featureTitle {
      background: ${({ theme }) => theme.colors.eucalyptus["30"]};
      color: ${({ theme }) => theme.colors.eucalyptus["80"]};
    }
  }

  .Features14__featureName {
    margin-top: 24px;
  }

  .Features14__featureDescription {
    margin-top: 8px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features14__feature {
      background: inherit;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    margin-top: 0;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    margin-top: 0;

    .Features14__title {
      display: none;
    }

    .Features14__mobileTitle {
      display: block;
    }

    .Features14__description {
      margin-top: 16px;
    }

    .Features14__featuresContainer {
      display: flex;
      flex-direction: column;
      margin-top: 40px;
    }

    .Features14__feature {
      padding: 40px 32px;
      margin-left: 0;
    }

    .Features14__feature:not(:first-child) {
      margin-left: 0;
      margin-top: 40px;
    }
  }
`;

gql`
  fragment Features14AContent on ComponentFeaturesFeatures14A {
    title
    description
    features14FeatureList {
      id
      featureTitle: title
      feature
      featureDescription: description
    }
  }
`;

export { Features14a };
export type { Props as Features14AProps };
